import React, { useState } from 'react';
import { KeyRound, Loader2, LogIn, ShieldCheck } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StaffLogin = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      onLogin(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top_left,_#1e3a5f,_#091a2b_55%,_#050b12)] px-4 py-8 sm:px-6 lg:px-10">
      <div className="mx-auto grid min-h-[calc(100vh-4rem)] max-w-6xl gap-6 lg:grid-cols-[1.05fr_0.95fr]">
        <section className="flex items-center rounded-[2rem] border border-white/10 bg-white/8 p-6 text-white shadow-2xl backdrop-blur-xl sm:p-8 lg:p-10" data-testid="staff-login-info-panel">
          <div className="max-w-2xl">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.24em] text-sky-200">
              <ShieldCheck className="h-4 w-4" /> AMC Staff Access
            </div>
            <h1 className="text-4xl font-semibold leading-tight sm:text-5xl" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Secure staff login with admin-controlled module access.
            </h1>
            <p className="mt-4 max-w-xl text-sm text-slate-200 sm:text-base">
              Sign in with your assigned account. Admin can manage staff, module permissions, and internal credentials from Staff Management after login.
            </p>

            <div className="mt-8 grid gap-4 sm:grid-cols-2" data-testid="staff-login-feature-list">
              <div className="rounded-2xl border border-white/10 bg-white/10 p-4">
                <p className="text-sm font-semibold text-white">Role-based access</p>
                <p className="mt-2 text-xs leading-5 text-slate-300">Appointments, accounts, health card, daily tasks, B2B and more can be turned on only by admin.</p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/10 p-4">
                <p className="text-sm font-semibold text-white">Internal credentials</p>
                <p className="mt-2 text-xs leading-5 text-slate-300">Seeded staff login details are now visible only after admin enters the portal.</p>
              </div>
            </div>
          </div>
        </section>

        <section className="flex items-center justify-center">
          <div className="w-full max-w-md rounded-[2rem] border border-slate-200 bg-white p-8 shadow-2xl sm:p-10" data-testid="staff-login-form-card">
            <div className="text-center">
              <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-3xl bg-[#0A2540]/6">
                <img
                  src="https://customer-assets.emergentagent.com/job_amc-care/artifacts/rt3uhs8c_AW_name%20board_curve%202X4.pdf%20%284%29.png"
                  alt="AMC Logo"
                  className="h-14 w-14"
                />
              </div>
              <h2 className="text-3xl font-semibold text-[#0A2540]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Staff Portal Login
              </h2>
              <p className="mt-2 text-sm text-slate-500">Aaric Medical Center administration access</p>
            </div>

            <form onSubmit={handleSubmit} className="mt-8 space-y-5" data-testid="staff-login-form">
              {error && (
                <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600" data-testid="staff-login-error">
                  {error}
                </div>
              )}

              <div>
                <Label htmlFor="staff-email">Username</Label>
                <div className="relative mt-2">
                  <KeyRound className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <Input
                    id="staff-email"
                    type="text"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Username or email"
                    required
                    className="h-12 rounded-2xl pl-10"
                    data-testid="staff-login-email"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="staff-password">Password</Label>
                <Input
                  id="staff-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  required
                  className="mt-2 h-12 rounded-2xl"
                  data-testid="staff-login-password"
                />
              </div>

              <Button type="submit" className="h-12 w-full rounded-2xl bg-[#0A2540] hover:bg-[#081b2f]" disabled={loading} data-testid="staff-login-submit">
                {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <LogIn className="mr-2 h-4 w-4" />}
                Sign In to Staff Portal
              </Button>
            </form>

            <div className="mt-6 rounded-2xl bg-slate-50 px-4 py-4 text-sm text-slate-600" data-testid="staff-login-help-note">
              Admin accounts can open Staff Management to view internal credentials and change module rights.
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default StaffLogin;